import { ChangeDetectorRef, ElementRef } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import {
  mergeMap,
  takeUntil,
  switchMap,
  first,
  map,
  scan,
} from 'rxjs/operators';
import { clamp, roundTo } from 'shared/utils';
// import { CrossBrowsing } from '../../constants/cross-browsing';

function getClientXFromMouseOrTouchEvent(
  event: MouseEvent | TouchEvent
): number {
  return event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
}

interface DragState {
  elementRect: DOMRect;
  clientX: number;
}

export abstract class PageSliderBaseComponent {
  protected abstract _barContainerElement: ElementRef<HTMLDivElement>;

  protected _unsubscriber = new Subject<void>();

  public _value = 0; // 0-1

  constructor(private _changeDetectorRef: ChangeDetectorRef) {}

  protected _setEventListeners(): void {
    // 마우스와 터치 이벤트를 모두 등록
    const mousedown$ = merge(
      fromEvent<MouseEvent>(
        this._barContainerElement.nativeElement,
        'mousedown'
      ),
      fromEvent<TouchEvent>(
        this._barContainerElement.nativeElement,
        'touchstart'
      )
    );

    const mousemove$ = merge(
      fromEvent<MouseEvent>(window, 'mousemove'),
      fromEvent<TouchEvent>(window, 'touchmove')
    );

    const mouseup$ = merge(
      fromEvent<MouseEvent>(window, 'mouseup'),
      fromEvent<TouchEvent>(window, 'touchend')
    );

    const dragstart$ = mousedown$.pipe(
      map((event) => {
        return (state: DragState): DragState =>
          Object.assign({}, state, {
            elementRect: (
              event.currentTarget as HTMLElement
            ).getBoundingClientRect(),
            clientX: getClientXFromMouseOrTouchEvent(event),
          });
      })
    );

    const dragging$ = mousedown$.pipe(
      mergeMap(() => {
        return mousemove$.pipe(
          takeUntil(mouseup$),
          map((event) => {
            return (state: DragState): DragState =>
              Object.assign({}, state, {
                clientX: getClientXFromMouseOrTouchEvent(event),
              });
          })
        );
      })
    );

    merge(dragstart$, dragging$)
      .pipe(
        scan((state, changeFn) => changeFn(state), {
          elementRect: {} as any,
          clientX: 0,
        }),
        map((state) => {
          const offsetX = state.clientX - state.elementRect.left;
          return roundTo(4, offsetX / state.elementRect.width);
        }),
        takeUntil(this._unsubscriber)
      )
      .subscribe((newPercent) => {
        this._onDrag(newPercent);
      });

    // 실제로 움직임이 있는 drag start
    mousedown$
      .pipe(
        switchMap(() => mousemove$.pipe(first(), takeUntil(mouseup$))),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        this._onDragStart();
      });

    mousedown$
      .pipe(
        switchMap(() => mouseup$.pipe(first())),
        takeUntil(this._unsubscriber)
      )
      .subscribe((event) => {
        this._onDragEnd(this._value);
      });
  }

  protected abstract _onDragStart(): void;

  protected _onDrag(newPercent: number): void {
    this._value = clamp(0, newPercent, 1);
    this._changeDetectorRef.detectChanges();
  }

  protected abstract _onDragEnd(newPercent: number): void;
}
